/**
 * **부트 전에 읽을 수 있어야 하는 문장.**
 *
 * ─── 왜 이것만 따로 있는가 ───
 * 사전은 `session.bootstrap` 응답으로 온다. 그런데 그 부트가 **실패한 화면**이야말로
 * 사용자가 문장을 가장 필요로 하는 자리다 — 서버가 죽었거나 로그인이 풀린 순간이다.
 * 응답에서 오는 사전을 기다리면 그 화면은 영원히 키를 그린다(`core.error.auth.unauthenticated`).
 *
 * ─── 왜 사본이 아닌가 ───
 * 손으로 옮겨 적으면 그 순간 두 번째 원본이 되고, 문장을 고치는 날 한쪽만 바뀐다.
 * 여기서는 **같은 객체에서 골라낸다** — 원본이 하나이므로 드리프트가 표현 불가능하다.
 * (kapella 가 색 드리프트 0을 유지한 방법과 같은 수법이다: 사본을 만들지 않는다)
 *
 * ─── 왜 `core.error.*` 전부인가 ───
 * 부트 실패의 `messageKey` 는 `classifyRpcError` 가 정한다. 즉 **어느 에러든 올 수 있다.**
 * 골라 담은 목록을 두면 그 목록에 없는 에러가 온 날 화면이 키를 그리고, 그건
 * 목록이 자라기만 하는 네 번째 손 관리 목록이 된다. 접두사 하나로 닫는 편이 싸다.
 *
 * 비용은 사전에 든 짧은 문장 20여 줄이 웹 번들에 들어가는 것뿐이다.
 */
import { coreMessagesKo } from './core.ko.js';
import type { MessageDictionary } from '../i18n.js';

/**
 * 접두사 **둘**이다.
 *
 * `core.error.` 는 위 주석의 그 이유다(어느 에러든 올 수 있으므로 목록 대신 접두사).
 * `core.screen.login.` 은 2026-09-05 에 왔다(0023): **로그인 화면은 정의상 부트 전이다.**
 * 사전이 부트 응답으로 오는데 그 부트가 401 이라서 뜨는 화면이므로, 자기 문장을 응답에서
 * 받을 방법이 원리적으로 없다. 그래서 화면 문장인데도 여기 실린다 —
 * 다른 `core.screen.*` 는 부트 뒤에 그려지므로 여기 오면 안 된다.
 *
 * `core.screen.password.` 는 2026-09-08 에 왔다(0036): 비밀번호 재설정 화면 둘도 **정의상
 * 부트 전이다** — 그 화면을 여는 사람은 로그인할 수 없어서 온 것이다. 접두사가 셋이 된 것을
 * 목록이 자라는 것으로 읽지 않는다: 이 목록은 정확히 **"부트 전에 그려지는 화면"의 목록**이고,
 * 그런 화면이 셋이 됐다.
 *
 * `core.screen.privacy.` 는 2026-09-11 에 왔다(0055): 개인정보 처리방침은 **회사 밖 사람이
 * 읽는 문서**다 — 명세서 메일의 만료 링크를 받은 사람(0044)에게는 계정이 없을 수 있다.
 * 넷째다.
 */
const PREBOOT_PREFIXES = [
  'core.error.', 'core.screen.login.', 'core.screen.password.', 'core.screen.privacy.',
  // 셀프 가입 · 소셜 · 초대(0225) — 셋 다 정의상 부트 전에 그려진다.
  'core.screen.signup.', 'core.screen.invite.', 'core.screen.social.',
] as const;

function pick(dict: MessageDictionary): MessageDictionary {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(dict)) {
    if (PREBOOT_PREFIXES.some((prefix) => k.startsWith(prefix))) out[k] = v;
  }
  return out;
}

/**
 * 부트 프레임이 쓰는 사전. 로케일 선택이 아직 없으므로 기본 로케일 하나다 —
 * 사용자별 로케일은 부트 응답이 정하고, 그건 정의상 부트가 성공한 뒤의 이야기다.
 */
export const PREBOOT_MESSAGES: MessageDictionary = pick(coreMessagesKo);
